import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Stack from "@mui/material/Stack";
import { MAAC_URL } from "@polifonia/env";
import { theme } from "@polifonia/theme";
import { fakeT } from "@polifonia/utils/react/fakeT";
import { useStatsigClient } from "@statsig/react-bindings";
import { Link } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { ExternalLink } from "@/components/ExternalLink";
import { InfoBox } from "@/components/InfoBox";
import { MotifIcon } from "@/components/MotifIcon";
import { Switch } from "@/components/Switch";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import { useEnableSrt10SunsetInfoAc } from "@/features/smart-redirect-tool/useEnableSrt10SunsetInfoAc";
import { useEnableWebChatBrandConfig } from "@/features/web-chat/useEnableWebChatBrandConfig";
import type { interludeClient } from "@/interlude";
import { interlude } from "@/interlude";
import { defineStyles } from "@/internal/emotion";
import { ChannelQueriesContext } from "@/pages/channels/pages/detail/channelQueriesContext";
import chatImg from "@/pages/channels/pages/detail/wccs/image/chat.png";
import srtImage from "@/pages/channels/pages/detail/wccs/image/srt.png";
import trackingImg from "@/pages/channels/pages/detail/wccs/image/tracking.png";

const styles = defineStyles({
  featureCard: css({
    display: "flex",
    gap: "16px",
    border: `1px solid ${theme.colors.neutral030}`,
    borderRadius: "12px",
    paddingLeft: "24px",
    overflow: "hidden",
  }),
  featureContent: css({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "20px 0",
  }),
  img: css({
    objectFit: "cover",
    width: "210px",
  }),
  bulletPointList: css({
    paddingLeft: "24px",
    listStyleType: "disc",
    margin: "0px",
  }),
});

const Layout: React.FC<{
  control: React.ReactNode;
  content: React.ReactNode;
  image: React.ReactNode;
}> = ({ control, content, image }) => {
  return (
    <div css={styles.featureCard}>
      <Stack direction="row" gap={3} flex={1}>
        {control ? (
          <Stack alignItems="center" justifyContent="center">
            {control}
          </Stack>
        ) : null}
        <div css={styles.featureContent}>{content}</div>
      </Stack>
      {image}
    </div>
  );
};

type AvailableModules =
  | "webChatModuleEnabled"
  | "webTrackingEnabled"
  | "smartRedirectToolEnabled";

interface ModuleSwitchProps {
  module: AvailableModules;
}

const t = fakeT;
const moduleTranslationMap: Record<
  ModuleSwitchProps["module"],
  Record<"enabled" | "disabled", string>
> = {
  webChatModuleEnabled: {
    enabled: t("page.channel.tools.chat.enabled"),
    disabled: t("page.channel.tools.chat.disabled"),
  },
  webTrackingEnabled: {
    enabled: t("page.channel.tools.tracking.enabled"),
    disabled: t("page.channel.tools.tracking.disabled"),
  },
  smartRedirectToolEnabled: {
    enabled: t("page.channel.tools.srt.enabled"),
    disabled: t("page.channel.tools.srt.disabled"),
  },
};

const moduleMapping = {
  webChatModuleEnabled: "wccsWebChatModuleEnabled",
  webTrackingEnabled: "wccsWebFootprintTrackingEnabled",
  smartRedirectToolEnabled: "wccsSmartRedirectToolEnabled",
} satisfies Record<
  ModuleSwitchProps["module"],
  keyof Awaited<
    ReturnType<typeof interludeClient.organization.organizationSetting>
  >
>;

const ModuleSwitch: React.FC<ModuleSwitchProps> = ({ module }) => {
  const { channel } = ChannelQueriesContext.useData();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const orgSettingQuery = interlude.organization.useOrganizationSetting();

  const orgSettingModuleEnabled = useMemo(() => {
    if (!orgSettingQuery.isSuccess) return false;
    return orgSettingQuery.data[moduleMapping[module]];
  }, [orgSettingQuery.isSuccess, orgSettingQuery.data, module]);

  const mutation = interlude.channel.useUpdateChannel(
    {
      params: {
        channelId: channel.id,
      },
    },
    {
      onSuccess(result) {
        if (result.type !== "wccs") return;
        const isEnabled = result.channelInformation[module];
        const messageKey =
          moduleTranslationMap[module][isEnabled ? "enabled" : "disabled"];
        enqueueSnackbar(t(messageKey), {
          variant: "success",
        });
      },
      onError() {
        enqueueSnackbar(t("common.apiError.unexpectedError"), {
          variant: "error",
        });
      },
    },
  );

  const checked = useMemo(() => {
    if (channel.type !== "wccs") return false;
    if (!orgSettingModuleEnabled) return false;
    return channel.channelInformation[module];
  }, [
    channel.type,
    channel.channelInformation,
    orgSettingModuleEnabled,
    module,
  ]);

  const handleChange = useHandler<
    React.ComponentProps<typeof Switch>["onChange"]
  >(function handleChange(_, enabled) {
    mutation.mutate({
      channelInformation: {
        [module]: enabled,
      },
    });
  });

  return (
    <Switch
      disabled={mutation.isPending || !orgSettingModuleEnabled}
      loading={mutation.isPending}
      checked={checked}
      onChange={handleChange}
    />
  );
};

const ModuleUpgradeBadge: React.FC<{ module: AvailableModules }> = ({
  module,
}) => {
  const orgSettingQuery = interlude.organization.useOrganizationSetting();

  const orgSettingModuleEnabled = useMemo(() => {
    if (!orgSettingQuery.isSuccess) return false;
    return orgSettingQuery.data[moduleMapping[module]];
  }, [orgSettingQuery.isSuccess, orgSettingQuery.data, module]);

  if (orgSettingModuleEnabled) return null;

  return (
    <Badge variant="attentive">
      <Trans i18nKey="page.channel.tools.badge.upgrade" />
    </Badge>
  );
};

export const Tools: React.FC = () => {
  const { t } = useTranslation();
  const statsigClient = useStatsigClient();
  const { channel } = ChannelQueriesContext.useData();
  const enableWebChatBrandConfig = useEnableWebChatBrandConfig();
  const orgSettingQuery = interlude.organization.useOrganizationSetting();
  /**
   * The customize page holds the brand config sections and the Footer
   * section, each behind its own permission, so either one opens it. The
   * page applies the same rule to a direct URL.
   */
  const canCustomizeWebChat =
    enableWebChatBrandConfig ||
    (orgSettingQuery.isSuccess &&
      orgSettingQuery.data.wccsCustomChatFooterEnabled);
  const hideSrtToggleInAc = useEnableSrt10SunsetInfoAc();

  return (
    <Stack direction="column" gap={2}>
      <Stack gap={3}>
        <Typography>{t("page.channel.tabs.tools.desc")}</Typography>
        <Layout
          control={<ModuleSwitch module="webChatModuleEnabled" />}
          content={
            <>
              <Stack gap={0.5}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" gap="4px" alignItems="center">
                    <Typography
                      variant="h3"
                      color={theme.colors.staticFgTitle}
                      fontWeight={500}
                    >
                      {t("page.channel.tools.chat.title")}
                    </Typography>
                    <ModuleUpgradeBadge module="webChatModuleEnabled" />
                  </Stack>
                  {!canCustomizeWebChat ? null : (
                    <Link
                      to="/channels/wccs/$id/web-chat-config"
                      params={{ id: channel.id }}
                    >
                      <Button variant="secondary">
                        {t("page.channel.tools.chat.customize")}
                      </Button>
                    </Link>
                  )}
                </Stack>
                <Typography>{t("page.channel.tools.chat.desc")}</Typography>
              </Stack>
              <Stack direction="row" gap={2} alignItems="center">
                <ExternalLink
                  href={t("page.channel.tools.chat.link")}
                  trailingIcon={true}
                >
                  {t("common.learnMore")}
                </ExternalLink>
              </Stack>
            </>
          }
          image={<img src={chatImg} css={styles.img} />}
        />
        <Layout
          control={<ModuleSwitch module="webTrackingEnabled" />}
          content={
            <>
              <Stack gap={0.5}>
                <Stack direction="row" gap="4px" alignItems="center">
                  <Typography
                    variant="h3"
                    color={theme.colors.staticFgTitle}
                    fontWeight={500}
                  >
                    {t("page.channel.tools.tracking.title")}
                  </Typography>
                  <ModuleUpgradeBadge module="webTrackingEnabled" />
                </Stack>
                <Typography>{t("page.channel.tools.tracking.desc")}</Typography>
                <ul css={styles.bulletPointList}>
                  <li>
                    <Typography>
                      {t("page.channel.tools.tracking.bulletPoint3")}
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      {t("page.channel.tools.tracking.bulletPoint2")}
                    </Typography>
                  </li>
                  <li>
                    <Stack direction="row" gap={1}>
                      <Typography>
                        {t("page.channel.tools.tracking.bulletPoint1")}
                      </Typography>
                      <Typography color={theme.colors.badgeBgActive}>
                        {t("common.comingSoon")}
                      </Typography>
                    </Stack>
                  </li>
                </ul>
              </Stack>
              <InfoBox
                variant="primary"
                description={
                  <Trans i18nKey="page.channel.tools.tracking.infoBox" />
                }
              />
              <Stack direction="row" gap={2}>
                <ExternalLink
                  href={t("page.channel.tools.tracking.link")}
                  trailingIcon={true}
                >
                  {t("common.learnMore")}
                </ExternalLink>
              </Stack>
            </>
          }
          image={<img src={trackingImg} css={styles.img} />}
        />

        {statsigClient.checkGate("smart_redirect_tool") && (
          <Layout
            control={
              hideSrtToggleInAc ? (
                // SRT 1.0 退場：控制權收回 MAAC，這裡顯示「已啟用但鎖定」的
                // disabled switch，讓使用者一眼看出已開啟、但無法在此關閉。
                // disabled 元素不發出 hover 事件，故用 span 包住作為 Tooltip 錨點。
                <Tooltip
                  title={t("page.channel.tools.binding.lockedSwitchTooltip", {
                    defaultValue: "此模組已預設啟用。",
                  })}
                  arrow
                  placement="top"
                >
                  <span
                    css={css`
                      display: inline-flex;
                      cursor: not-allowed;
                    `}
                  >
                    <Switch checked disabled />
                  </span>
                </Tooltip>
              ) : (
                <ModuleSwitch module="smartRedirectToolEnabled" />
              )
            }
            content={
              <>
                <Stack gap={0.5}>
                  <Stack direction="row" gap="4px" alignItems="center">
                    <Typography
                      variant="h3"
                      color={theme.colors.staticFgTitle}
                      fontWeight={500}
                    >
                      {t("page.channel.tools.binding.title")}
                    </Typography>
                    {hideSrtToggleInAc ? null : <Badge>New</Badge>}
                    {hideSrtToggleInAc ? (
                      <Tooltip
                        title={t(
                          "page.channel.tools.binding.controlMovedToMaacTooltip",
                          {
                            defaultValue:
                              "如需開啟或關閉，請至 MAAC 的「網站導流工具」設定。",
                          },
                        )}
                        arrow
                        placement="top"
                      >
                        <Stack
                          component="span"
                          css={css`
                            color: ${theme.colors.staticFgNote};
                            font-size: 16px;
                            cursor: pointer;
                          `}
                        >
                          <MotifIcon un-i-motif="circle_info" />
                        </Stack>
                      </Tooltip>
                    ) : null}
                  </Stack>
                  <Typography>
                    {t("page.channel.tools.binding.desc")}
                  </Typography>
                </Stack>
                <InfoBox
                  variant="primary"
                  description={
                    hideSrtToggleInAc ? (
                      t("page.channel.tools.binding.controlMovedToMaac", {
                        defaultValue:
                          "此模組已預設啟用。是否於官網顯示,請至 MAAC 的「網站導流工具」設定與控制。",
                      })
                    ) : (
                      <Trans i18nKey="page.channel.tools.binding.infoBox" />
                    )
                  }
                />
                <Stack direction="row" gap={2}>
                  <ExternalLink
                    href={t("page.channel.tools.binding.link")}
                    trailingIcon={true}
                  >
                    {t("common.learnMore")}
                  </ExternalLink>
                  {hideSrtToggleInAc && MAAC_URL ? (
                    <ExternalLink
                      href={`${MAAC_URL}/widget`}
                      trailingIcon={true}
                    >
                      {t("page.channel.tools.binding.goToMaacWidget", {
                        defaultValue: "前往網站導流工具設定",
                      })}
                    </ExternalLink>
                  ) : null}
                </Stack>
              </>
            }
            image={<img src={srtImage} css={styles.img} />}
          />
        )}
      </Stack>
    </Stack>
  );
};
